import { db } from '../config/firebase';
import {
  collection, getDocs, doc, updateDoc, deleteDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { C, individualReportId } from './programData';

// ══════════════════════════════════════════════════════════════════════
//  KUONDOA WANACHAMA WANAORUDIA (marudio)
//  Staff tofauti wamekuwa wakisajili mtu yuleyule mara kadhaa bila
//  kujua tayari yupo. Hii inasababisha idadi ya uongo kwenye takwimu.
//
//  Zana hii: (1) inatambua vikundi vya majina yanayofanana, (2) admin
//  anachagua rekodi ya kubaki nayo, (3) ripoti zote za nakala zinahamia
//  kwenye rekodi hiyo moja badala ya kupotea.
// ══════════════════════════════════════════════════════════════════════

export function normalizeName(s) {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// Hupata vikundi vya wanachama wenye jina na aina moja — hawa ndio
// wanaoshukiwa kuwa mtu mmoja aliyesajiliwa zaidi ya mara moja.
export async function findDuplicateGroups() {
  const [memberSnap, reportSnap] = await Promise.all([
    getDocs(collection(db, C.members)),
    getDocs(collection(db, C.reports)),
  ]);

  const members = memberSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const reports = reportSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const countByMember = {};
  reports.forEach(r => {
    if (r.memberId) countByMember[r.memberId] = (countByMember[r.memberId] || 0) + 1;
    if (r.attendance) {
      Object.keys(r.attendance).forEach(mid => {
        countByMember[mid] = (countByMember[mid] || 0) + 1;
      });
    }
  });

  const byKey = {};
  members.forEach(m => {
    if (m.active === false) return;
    const nk = normalizeName(m.fullName);
    if (!nk) return;
    const key = `${nk}__${m.type}`;
    (byKey[key] = byKey[key] || []).push({
      ...m,
      reportCount: countByMember[m.id] || 0,
    });
  });

  return Object.values(byKey)
    .filter(list => list.length > 1)
    .map(list => list.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)));
}

// ── Kuunganisha rekodi za nakala kuwa moja ─────────────────────────────
// keepId: rekodi itakayobaki. mergeIds: rekodi za nakala zitakazoondolewa
// baada ya ripoti zake kuhamishwa kwenye keepId.
export async function mergeMembers({ keepId, mergeIds, author }) {
  const [memberSnap, reportSnap] = await Promise.all([
    getDocs(collection(db, C.members)),
    getDocs(collection(db, C.reports)),
  ]);

  const keepDoc = memberSnap.docs.find(d => d.id === keepId);
  if (!keepDoc) throw new Error('Rekodi ya kubaki nayo haikupatikana.');
  const keepData = keepDoc.data();
  const reports = reportSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  let movedIndividual = 0;
  let movedAsContribution = 0;
  let movedAttendance = 0;
  const unionProgramIds = new Set(keepData.programIds || []);
  let clubId = keepData.clubId || '';
  let clubName = keepData.clubName || '';

  for (const dupId of mergeIds) {
    if (dupId === keepId) continue;

    // ── Ripoti binafsi za rekodi ya nakala ──────────────────────────
    const own = reports.filter(r => r.kind === 'individual' && r.memberId === dupId);
    for (const r of own) {
      const canonicalId = individualReportId(r.programId, keepId, r.weekKey);
      const existing = reports.find(x => x.id === canonicalId);

      if (existing) {
        // Keep tayari ina ripoti ya wiki hiyo — weka ya nakala kama
        // nyongeza badala ya kuipoteza kabisa.
        const summary = Object.entries(r.values || {})
          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
          .map(([k, v]) => `${k}: ${v}`).join(' · ');
        await updateDoc(doc(db, C.reports, canonicalId), {
          contributions: [
            ...(existing.contributions || []),
            {
              by: 'system', byName: 'Imehamishwa (ripoti pacha)',
              text: `Ripoti nyingine ya wiki hii ilipatikana kwenye rekodi iliyoondolewa: ${summary || '(hakuna maelezo)'}`,
              at: new Date().toISOString(),
            },
          ],
          updatedAt: serverTimestamp(),
        });
        movedAsContribution++;
      } else {
        // Hamishia ripoti kwenye ID sahihi ya kudumu ya rekodi ya kubaki
        const { id: _oldId, ...rest } = r;
        await setDoc(doc(db, C.reports, canonicalId), {
          ...rest,
          memberId: keepId,
          memberName: keepData.fullName,
        });
        movedIndividual++;
      }
      await deleteDoc(doc(db, C.reports, r.id));
    }

    // ── Ripoti za session zenye mahudhurio ya rekodi ya nakala ──────
    const sessionReports = reports.filter(
      r => r.kind === 'session' && r.attendance && r.attendance[dupId] !== undefined
    );
    for (const r of sessionReports) {
      const att = { ...r.attendance };
      if (att[keepId] === undefined) {
        att[keepId] = att[dupId];
        movedAttendance++;
      }
      delete att[dupId];
      await updateDoc(doc(db, C.reports, r.id), { attendance: att });
    }

    // ── Kusanya programIds na club kutoka kwenye rekodi ya nakala ───
    const dupDoc = memberSnap.docs.find(d => d.id === dupId);
    if (dupDoc) {
      const dd = dupDoc.data();
      (dd.programIds || []).forEach(p => unionProgramIds.add(p));
      if (!clubId && dd.clubId) { clubId = dd.clubId; clubName = dd.clubName || ''; }
    }

    await deleteDoc(doc(db, C.members, dupId));
  }

  await updateDoc(doc(db, C.members, keepId), {
    programIds: Array.from(unionProgramIds),
    clubId, clubName,
    mergedFrom: [...(keepData.mergedFrom || []), ...mergeIds],
    mergedAt: serverTimestamp(),
    mergedBy: author?.name || '',
    updatedAt: serverTimestamp(),
  });

  return { movedIndividual, movedAsContribution, movedAttendance, removed: mergeIds.length };
}
